import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from './api';
import { ApexBrowser } from './ApexBrowser';
import { IntercardBrowser } from './IntercardBrowser';

/**
 * "APEX" on System status (owner 10/26), admins only: can MyWunderland read each store's sales from APEX, and is
 * every register saving its receipt files (the backup copies) on the NAS. "Test APEX connection" checks now;
 * the app also checks by itself every 15 minutes in the day and the registers at 4:30 AM, and texts admins.
 */
interface Explained { title: string; what: string; likely: string[]; fix: string[]; code?: string }
interface Reg { register: number; apex: number; files: number | null; state: 'ok' | 'unused' | 'no-files' | 'few-files'; note: string }
interface Store {
  store: string; locationId: number; setUp: boolean; host?: string; state: 'ok' | 'warn' | 'bad' | 'off'; summary: string;
  problem?: Explained | null; registers?: Reg[]; registerProblems?: Explained[]; receipts?: string; receiptsBad?: boolean;
  lastAuto?: string | null; lastNightly?: string | null;
  layout?: Record<string, string[]>;
  renames?: { file: string; old: string; new: string; at: string }[]; grants?: string | null; canFix?: boolean;
  sales?: SalesDay[];
}
interface Row { what: string; apex: number; files: number | null; same: boolean | null; money: boolean }
interface SalesDay { day: string; label: string; at: string | null; filesAt: string | null; rows: Row[]; differ: number; probe: unknown;
  records?: { onlyApex: string[]; onlyFiles: string[]; notSame: string[]; odd: string[] }; apexDocs?: number; fileDocs?: number;
  warnings?: { key: string; what: string; n: number; total: number; sample: number[]; checked: string | null }[] }
interface Resp { day: string; stores: Store[]; anySetUp: boolean; clock?: { nas: string; offMinutes: number }; checkedAt?: string; helper?: { configured: boolean } }
interface Helper { configured: boolean; ok: boolean; version?: string; stores?: { store: string; ok: boolean; message: string | null }[]; problem?: string }
interface Pull { requestedAt: string; ageSeconds: number; stores: { store: string; done: boolean; at: string }[] }

const DOT: Record<string, string> = { ok: '#1E8E4E', warn: '#C27C0E', bad: '#C0392B', off: '#9AA3AC' };

const show = (n: number | null, money: boolean) => n == null ? '—' : money ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : n.toLocaleString('en-US');

/** Sales read straight from APEX for a day, next to the same numbers added up from the receipt files. */
function SalesCheck({ d, locationId, reload, showToast }: { d: SalesDay; locationId: number; reload: () => void; showToast: (m: string) => void }) {
  const noFiles = d.rows.every((r) => r.files == null);
  const [busy, setBusy] = useState('');
  const mark = async (key: string, undo: boolean) => {
    const note = undo ? undefined : window.prompt('What did you find? (optional, e.g. “cash put in the drawer, not a sale”)') ?? undefined;
    if (note === undefined && !undo) return;
    setBusy(key);
    try { await post('/api/admin/apex/sales-checked', { locationId, key, note, undo }); reload(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save.'); } finally { setBusy(''); }
  };
  const ws = d.warnings ?? [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 14 }}>
        <b>Sales read straight from APEX · {d.label} {d.day}</b>{d.at && <span style={{ color: '#4A5561' }}> (read {d.at})</span>}{' '}
        {noFiles ? <span style={{ color: '#4A5561' }}>No receipt files for this day to compare with yet.</span>
          : d.differ === 0 ? <span style={{ color: DOT.ok }}>✓ Matches the receipt files</span>
          : <span style={{ color: DOT.warn, fontWeight: 600 }}>⚠ {d.differ} {d.differ === 1 ? 'number differs' : 'numbers differ'} from the receipt files. Reports use APEX’s numbers. A sale rung in the last few minutes, or a register that skipped a receipt file, shows here; anything else, send Claude a screenshot.</span>}
      </span>
      <table className="apexregs">
        <thead><tr><th /><th>From APEX</th><th>From receipt files</th><th /></tr></thead>
        <tbody>{d.rows.map((r) => (
          <tr key={r.what}>
            <td>{r.what}</td><td>{show(r.apex, r.money)}</td><td>{show(r.files, r.money)}</td>
            <td>{r.same === true ? <span style={{ color: DOT.ok }}>✓</span> : r.same === false ? <span style={{ color: DOT.warn, fontWeight: 600 }}>⚠ differs</span> : ''}</td>
          </tr>
        ))}</tbody>
      </table>
      {ws.length > 0 && (
        <div style={{ fontSize: 13, border: `1px solid ${ws.some((w) => !w.checked) ? DOT.warn : '#D5DAD3'}`, borderRadius: 6, padding: '6px 10px', background: ws.some((w) => !w.checked) ? '#FFF8E6' : '#F7F8F6' }}>
          <b>Sales-related records Reports don’t count</b> <span style={{ color: '#4A5561' }}>(Reports count finished receipts only. Check each kind once: if it should count, tell Claude.)</span>
          {ws.map((w) => (
            <div key={w.key} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 4, color: w.checked ? '#4A5561' : '#17212B' }}>
              <span>{w.checked ? '✓' : '⚠'} {w.n} × {w.what}: {show(w.total / 100, true)}{w.sample.length ? ` · APEX # ${w.sample.join(', ')}` : ''}</span>
              {w.checked
                ? <><span style={{ fontSize: 12 }}>Checked by {w.checked}</span><button className="btn2" style={{ fontSize: 12, padding: "2px 8px" }} disabled={busy === w.key} onClick={() => void mark(w.key, true)}>Show again</button></>
                : <button className="btn2" style={{ fontSize: 12, padding: "2px 8px" }} disabled={busy === w.key} onClick={() => void mark(w.key, false)}>Checked, it’s fine</button>}
            </div>
          ))}
        </div>
      )}
      <span style={{ fontSize: 12.5, color: '#4A5561' }}>Record check: {d.apexDocs ?? 0} APEX records, {d.fileDocs ?? 0} receipt files with record numbers.</span>
      {d.records && d.differ > 0 && (d.records.onlyApex.length + d.records.onlyFiles.length + d.records.notSame.length + d.records.odd.length) > 0 && (
        <details open style={{ fontSize: 12.5, color: '#4A5561' }}>
          <summary style={{ cursor: 'pointer' }}>Records that don’t line up (# = APEX’s own record number; “receipt” = the number printed on the receipt, the one Find a receipt looks up)</summary>
          {([['In APEX, no receipt file', d.records.onlyApex], ['Receipt file, not counted from APEX', d.records.onlyFiles],
            ['In both, but the amounts differ', d.records.notSame], ['APEX records whose items or payments don’t add up to the total', d.records.odd]] as const)
            .filter(([, xs]) => xs.length).map(([title, xs]) => (
              <div key={title} style={{ marginTop: 6 }}>
                <b>{title} ({xs.length})</b>
                <pre className="apexcode" style={{ background: '#F1F3EF', color: '#17212B', whiteSpace: 'pre-wrap', margin: '2px 0' }}>
                  {xs.slice(0, 15).join('\n')}{xs.length > 15 ? `\n…and ${xs.length - 15} more` : ''}
                </pre>
              </div>
            ))}
        </details>
      )}
      {d.probe != null && (
        <details style={{ fontSize: 12.5, color: '#4A5561' }}>
          <summary style={{ cursor: 'pointer' }}>APEX totals by type (for Claude: no customer names)</summary>
          <pre className="apexcode" style={{ background: '#F1F3EF', color: '#17212B', whiteSpace: 'pre-wrap' }}>{JSON.stringify(d.probe)}</pre>
        </details>
      )}
    </div>
  );
}

function Fix({ e }: { e: Explained }) {
  return (
    <div className="apexfix">
      <b>{e.title}</b>
      <span>{e.what}</span>
      <span className="apexh">Likely causes</span>
      <ul>{e.likely.map((x, i) => <li key={i}>{x}</li>)}</ul>
      <span className="apexh">What to check</span>
      <ol>{e.fix.map((x, i) => <li key={i}>{x}</li>)}</ol>
      {e.code && <pre className="apexcode">{e.code}</pre>}
    </div>
  );
}

export function ApexStatus({ showToast }: { showToast: (m: string) => void }) {
  const [d, setD] = useState<Resp | null>(null);
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState(false);
  const load = useCallback(async () => { try { setD(await get<Resp>('/api/admin/apex')); } catch { /* the rest of the page still works */ } }, []);
  useEffect(() => { void load(); }, [load]);
  const test = async () => {
    setBusy(true);
    try { setD(await post<Resp>('/api/admin/apex/test')); setTested(true); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'The test didn’t run.'); }
    finally { setBusy(false); }
  };
  // "Pull receipt files now": the receipts helpers look for the request every 15 seconds; watch until each store's
  // folder has been read (or 2 minutes), then check APEX again so the comparison uses the new files.
  const [pull, setPull] = useState<Pull | null>(null);
  const [pulling, setPulling] = useState(false);
  const pullNow = async () => {
    setPulling(true);
    try {
      let p = (await post<{ pull: Pull }>('/api/admin/apex/pull')).pull;
      setPull(p);
      for (let i = 0; i < 24 && p.stores.some((x) => !x.done); i++) {
        await new Promise((r) => setTimeout(r, 5000));
        p = (await get<{ pull: Pull }>('/api/admin/apex/pull')).pull;
        setPull(p);
      }
      if (p.stores.some((x) => !x.done)) showToast('Not every store’s receipt files came in within 2 minutes. The list below says which.');
      await test();
    } catch (e) { showToast(e instanceof ApiError ? e.message : 'Couldn’t ask for the receipt files.'); }
    finally { setPulling(false); }
  };
  // the permissions helper on Beaverton (owner 10/29)
  const [helper, setHelper] = useState<Helper | null>(null);
  const [checking, setChecking] = useState(false);
  const checkHelper = async () => {
    setChecking(true);
    try { setHelper((await get<{ helper: Helper }>('/api/admin/apex/helper')).helper); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'Couldn’t check the helper.'); }
    finally { setChecking(false); }
  };
  const [fixing, setFixing] = useState<number | null>(null);
  const fix = async (locationId: number) => {
    setFixing(locationId);
    try {
      const r = await post<{ store: Store; notice: string }>('/api/admin/apex/fix', { locationId });
      setD((x) => (x ? { ...x, stores: x.stores.map((s) => (s.locationId === locationId ? r.store : s)) } : x));
      showToast(r.notice);
    } catch (e) { showToast(e instanceof ApiError ? e.message : 'The fix didn’t run.'); }
    finally { setFixing(null); }
  };
  const [browsing, setBrowsing] = useState(false);
  const [icBrowsing, setIcBrowsing] = useState(false);
  // the kiosk sales backup (owner 10/29): ooo.wunderlandgames.com and Intercard directly, side by side
  const [kc, setKc] = useState<{ today: string; stores: { store: string; kiosk: string; server: string; direct: string }[] } | null>(null);
  const [kcBusy, setKcBusy] = useState(false);
  const kioskCheck = async () => {
    setKcBusy(true);
    try { setKc(await post('/api/admin/apex/kiosk-check')); } catch (e) { showToast(e instanceof ApiError ? e.message : 'The check didn’t run.'); } finally { setKcBusy(false); }
  };
  if (!d) return null;
  const on = d.stores.filter((s) => s.setUp), off = d.stores.filter((s) => !s.setUp);
  return (
    <div className="stcard" id="apex">
      <span className="tlabel">APEX (sales for Reports)</span>
      <span style={{ color: '#4A5561', fontSize: 14 }}>
        Reports read each store’s sales straight from its APEX computer, with a read-only login. The registers also save a receipt file
        for every sale on the NAS, as a backup. The app checks both by itself (every 15 minutes in the day, and every register at 4:30 AM)
        and texts admins if something stops working.
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button className="btn2" disabled={busy || !d.anySetUp} onClick={() => void test()}>{busy ? 'Testing… (up to 10 seconds)' : 'Test APEX connection'}</button>
        {!d.anySetUp && <span style={{ fontSize: 13, color: '#4A5561' }}>No store has its APEX lines in the NAS settings file yet (APEX_DB_HOST_SALEM and so on).</span>}
        <button className="btn2" disabled={pulling || busy} onClick={() => void pullNow()}>{pulling ? 'Pulling receipt files…' : 'Pull receipt files now'}</button>
        {tested && <span style={{ fontSize: 13, color: '#4A5561' }}>Checked at {d.checkedAt ?? 'just now'} by the {d.clock ? d.clock.nas.charAt(0).toUpperCase() + d.clock.nas.slice(1) : ''} NAS · today is {d.day}</span>}
        {d.clock && Math.abs(d.clock.offMinutes) >= 3 && <span style={{ fontSize: 13, color: DOT.warn, fontWeight: 600 }}>⚠ The {d.clock.nas} NAS’s clock is {Math.abs(d.clock.offMinutes)} minutes {d.clock.offMinutes < 0 ? 'behind' : 'ahead of'} the database’s.</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 13, color: '#4A5561' }}>
        <span><b style={{ color: '#17212B' }}>Permissions helper (Beaverton NAS):</b>{' '}
          {d.helper?.configured ? 'set up. When an APEX update adds or renames a table, it lets MyWunderland read it by itself, and the app switches to a renamed table once its sales check out.' : 'not set up yet (GRANTS_HELPER_URL and GRANTS_SECRET in the NAS settings file). Until it is, new permissions are typed in by hand with the lines shown below.'}</span>
        {d.helper?.configured && <button className="btn2" disabled={checking} onClick={() => void checkHelper()}>{checking ? 'Checking…' : 'Check helper'}</button>}
      </div>
      {helper && (
        <span style={{ fontSize: 13, color: helper.ok ? DOT.ok : DOT.warn, fontWeight: helper.ok ? 400 : 600 }}>
          {helper.problem ? `⚠ ${helper.problem}` : `${helper.ok ? '✓' : '⚠'} Helper answered (version ${helper.version}): ${(helper.stores ?? []).map((x) => `${x.store.charAt(0).toUpperCase() + x.store.slice(1)} ${x.ok ? 'signs in to APEX' : `can’t sign in to APEX (${x.message})`}`).join(' · ') || 'no stores set up in its settings file'}`}
        </span>
      )}
      {pull && (
        <span style={{ fontSize: 13, color: '#4A5561' }}>
          Receipt files asked for at {pull.requestedAt}: {pull.stores.map((x) => `${x.store} ${x.done ? `✓ ${x.at}` : 'waiting…'}`).join(' · ')}
        </span>
      )}
      {on.map((s) => (
        <div key={s.store} className="apexstore">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 5, background: DOT[s.state] }} />
            <b style={{ fontSize: 16 }}>{s.store}</b>
            <span style={{ color: s.state === 'bad' ? DOT.bad : '#4A5561' }}>{s.summary}</span>
          </span>
          {s.problem && <Fix e={s.problem} />}
          {s.canFix && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn" disabled={fixing !== null} onClick={() => void fix(s.locationId)}>{fixing === s.locationId ? 'Asking the Beaverton helper…' : 'Fix permissions now'}</button>
              <span style={{ fontSize: 13, color: '#4A5561' }}>Asks the permissions helper on Beaverton to let MyWunderland read the tables above. It also happens by itself within an hour.</span>
            </div>
          )}
          {s.renames?.map((x) => <span key={x.old} style={{ fontSize: 13, color: '#4A5561' }}>APEX update: reading {x.new} instead of {x.old} since {x.at} (switched by itself after checking its sales).</span>)}
          {s.grants && <span style={{ fontSize: 12.5, color: '#4A5561' }}>Permissions helper, last asked {s.grants}.</span>}
          {s.registers && s.registers.length > 0 && (
            <table className="apexregs">
              <thead><tr><th>Register</th><th>Sales in APEX today</th><th>Receipt files today</th><th /></tr></thead>
              <tbody>{s.registers.map((r) => (
                <tr key={r.register} className={r.state}>
                  <td>Register {r.register}</td>
                  <td>{r.state === 'unused' ? '—' : r.apex}</td>
                  <td>{r.files == null ? 'not compared' : r.state === 'unused' ? '—' : r.files}</td>
                  <td>{r.state === 'ok' && r.files == null ? <span style={{ color: '#4A5561' }}>{r.note}</span> : r.state === 'ok' ? <span style={{ color: DOT.ok }}>✓ {r.note}</span> : r.state === 'unused' ? <span style={{ color: '#4A5561' }}>Not used today</span> : <span style={{ color: DOT.warn, fontWeight: 600 }}>⚠ {r.note}</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {s.receipts && <span style={{ fontSize: 13, color: s.receiptsBad ? DOT.warn : '#4A5561', fontWeight: s.receiptsBad ? 600 : 400 }}>{s.receiptsBad ? '⚠ ' : ''}{s.receipts}</span>}
          {s.registerProblems?.map((e, i) => <Fix key={i} e={e} />)}
          {s.sales?.map((d) => <SalesCheck key={d.day} d={d} locationId={s.locationId} reload={() => void load()} showToast={showToast} />)}
          {s.layout && Object.keys(s.layout).length > 0 && (
            <details style={{ fontSize: 12.5, color: '#4A5561' }}>
              <summary style={{ cursor: 'pointer' }}>APEX table layout (for Claude: column names only, no data)</summary>
              <pre className="apexcode" style={{ background: '#F1F3EF', color: '#17212B' }}>{Object.entries(s.layout).map(([t, cols]) => `${t}: ${cols.join(', ')}`).join('\n')}</pre>
            </details>
          )}
          {(s.lastAuto || s.lastNightly) && (
            <span style={{ fontSize: 12.5, color: '#4A5561' }}>
              {s.lastAuto && <>Last check {s.lastAuto}.</>} {s.lastNightly && <>Last register check {s.lastNightly}.</>}
            </span>
          )}
        </div>
      ))}
      {off.length > 0 && <span style={{ fontSize: 13, color: '#4A5561' }}>Not connected to APEX: {off.map((s) => s.store).join(', ')}.</span>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span><b style={{ color: '#17212B' }}>Kiosk sales:</b> read from the kiosk sales server (ooo.wunderlandgames.com); if it doesn’t answer, straight from Intercard.</span>
          <button className="btn2" disabled={kcBusy} onClick={() => void kioskCheck()}>{kcBusy ? 'Checking…' : 'Check kiosk sales'}</button>
        </div>
        {kc?.stores.map((x) => <span key={x.store}>{x.store} (kiosk {x.kiosk}) today: kiosk sales server {x.server} · Intercard directly {x.direct}</span>)}
        <button className="btn2" style={{ alignSelf: 'flex-start' }} onClick={() => setIcBrowsing((x) => !x)}>{icBrowsing ? 'Close the Intercard browser' : 'Browse Intercard data…'}</button>
        {icBrowsing && <IntercardBrowser showToast={showToast} />}
      </div>
      {on.length > 0 && <button className="btn2" style={{ alignSelf: 'flex-start' }} onClick={() => setBrowsing((x) => !x)}>{browsing ? 'Close the APEX browser' : 'Browse APEX data…'}</button>}
      {browsing && <ApexBrowser stores={on.map((s) => ({ locationId: s.locationId, store: s.store }))} showToast={showToast} />}
    </div>
  );
}
